import { CountMetric, LatencyMetric, EnvironmentDetail, UnsupportedEnvironmentException, PromiseUtils } from '@amzn/ihm-associate-tools-js-lib'
import * as CandyCaneService from '@services/CandyCaneService'
import { CandyCaneBluetoothErrorMappings } from '@services/bluetooth/CandyCaneBluetoothErrorMappings'

const BluetoothLowEnergyConnectionTimeout = Object.freeze({
    DEFAULT: 30000,
})

const programName = 'IhmFlyingDutchman'

export class BluetoothLowEnergyService {

    // Public functions

    /**
     * Attempts to connect a given Bluetooth Low Energy device.
     * @param args Argument requires macAddress and bluetooth profile for connection for example,
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A",
     *     profile: "GATT"
     *   }
     * ]
     *
     * @return {Promise}    - success when the device is connected.
     *                      - exception when plugin returns error
     */

    public static connectBle (args) {

        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const connectBleFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.connectBle')
        const connectBleLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.connectBle')
        connectBleLatencyMetrics.start()
        console.info('ATBase BluetoothLowEnergyService connectBle CandyCaneService.bluetoothConnectBle: ' + JSON.stringify(args))
        const bluetoothConnectBlePromise = CandyCaneService.bluetoothConnectBle(args)
            .then(() => {
                console.info('BluetoothLowEnergy device connection was successful')
                connectBleFaultMetrics.count(0)
            })

        return PromiseUtils.executeWithTimeout(bluetoothConnectBlePromise, {
            durationMillis: BluetoothLowEnergyConnectionTimeout.DEFAULT,
            error: new Error('BluetoothLowEnergy connectBle() execution timed out.'),
        })
            .catch((error) => {
                console.error('BluetoothLowEnergy pairing unsuccessful with error: ',error)
                connectBleFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            }).finally(() => {
                connectBleLatencyMetrics.stop()
            })
    }

    /**
     * Closes bluetooth gatt connection from BLE device
     *
     * @return {Promise}     - success when the device is disconnected.
     *                       - exception when plugin returns error
     */

    public static disconnectBle (args) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth disconnect is only supported on Android.',
            }))
        }
        const disconnectBleFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.disconnectBle')
        const disconnectBleLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.disconnectBle')
        disconnectBleLatencyMetrics.start()
        console.info('ATBase BluetoothLowEnergyService disconnectBle CandyCaneService.bluetoothDisconnectBle: ' + JSON.stringify(args))
        return CandyCaneService.bluetoothDisconnectBle(args)
            .then(() => {
                console.info('BluetoothLowEnergy device disconnection was successful')
                disconnectBleFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('BluetoothLowEnergy disconnect failure with error: ',error)
                disconnectBleFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                disconnectBleLatencyMetrics.stop()
            })
    }

    /**
     * Reads the Characteristic value from a BLE device with a given mac address and returns the response.
     *
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A",
     *     serviceUuid: "1234567a-123a-1234-1abc-adc123456789",
     *     characteristicUuid: "1234567a-123a-1234-1abc-adc123456789",
     *     charsetName: "UTF8"
     *   }
     * ]
     *
     * @return {Promise}    - success when the characteristic value is successfully read.
     *                      - exception when plugin returns error
     */

    public static readCharacteristicBle (args) {

        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const readCharacteristicBleFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.readCharacteristicBle')
        const readCharacteristicBleLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.readCharacteristicBle')
        readCharacteristicBleLatencyMetrics.start()
        console.info('ATBase BluetoothLowEnergyService readCharacteristicBle CandyCaneService.bluetoothReadCharacteristicBle: ' + JSON.stringify(args))
        const bluetoothReadCharacteristicBlePromise = CandyCaneService.bluetoothReadCharacteristicBle(args)
            .then((data) => {
                console.info('BluetoothLowEnergy data was successfully retrieved')
                readCharacteristicBleFaultMetrics.count(0)
                return data
            })

        return PromiseUtils.executeWithTimeout(bluetoothReadCharacteristicBlePromise, {
            durationMillis: BluetoothLowEnergyConnectionTimeout.DEFAULT,
            error: new Error('BluetoothLowEnergy readCharacteristicBle() execution timed out.'),
        })
            .catch((error) => {
                console.error('BluetoothLowEnergy readCharacteristicBle failure with error: ', error)
                readCharacteristicBleFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                readCharacteristicBleLatencyMetrics.stop()
            })
    }

    /**
     * Writes the Characteristic value to a BLE device.
     *
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A",
     *     serviceUuid: "1234567a-123a-1234-1abc-adc123456789",
     *     characteristicUuid: "1234567a-123a-1234-1abc-adc123456789",
     *     value: "1",
     *     charsetName: "UTF8"
     *   }
     * ]
     *
     * @return {Promise}     - success when the characteristic value is successfully written.
     *                       - exception when plugin returns error
     */

    public static writeCharacteristicBle (args) {

        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const writeCharacteristicBleFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.writeCharacteristicBle')
        const writeCharacteristicBleLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.writeCharacteristicBle')
        writeCharacteristicBleLatencyMetrics.start()
        console.info('ATBase BluetoothLowEnergyService writeCharacteristicBle CandyCaneService.bluetoothWriteCharacteristicBle: ' + JSON.stringify(args))
        return CandyCaneService.bluetoothWriteCharacteristicBle(args)
            .then(() => {
                console.info('BluetoothLowEnergy data writing was successful')
                writeCharacteristicBleFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('BluetoothLowEnergy writeCharacteristicBle failue with error: ', error)
                writeCharacteristicBleFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                writeCharacteristicBleLatencyMetrics.stop()
            })
    }

}
